import { BreakCodeClipboardEntry } from './break.model'

/**
 * Shown when a copied code is not an available option in the target dialog.
 * Deliberately hedged rather than the ticket AC's "Cannot paste Break Codes": a
 * rejected code does NOT abort the paste — the other code and the note still land, and
 * the note is the field most likely to matter.
 */
export const BREAK_CODES_NOT_PASTED = 'Some break codes could not be pasted'

export interface PasteResolution {
  actionCode: string | null
  descriptionCode: string | null
  note: string
  error: string | null
}

/**
 * Decide what a paste writes into a dialog, given the codes that dialog offers.
 *
 * Strict mirror (PVAL-26114): a null clipboard code CLEARS the target box, writing only
 * to controls the user can still see and edit before Save. A code that is not an
 * available option is NOT applied — its box is cleared and `error` is set. The note
 * always pastes, even when a code was rejected.
 *
 * Availability must be decided here rather than deferred to form validation because
 * ng-select renders a value outside its `[items]` as blank, so the paste would
 * otherwise vanish silently.
 */
export function resolvePaste(
  entry: BreakCodeClipboardEntry,
  actionOptions: string[],
  descriptionOptions: string[],
): PasteResolution {
  const action = resolveCode(entry.actionCode, actionOptions)
  const description = resolveCode(entry.descriptionCode, descriptionOptions)

  return {
    actionCode: action.code,
    descriptionCode: description.code,
    note: entry.note,
    error:
      action.rejected || description.rejected ? BREAK_CODES_NOT_PASTED : null,
  }
}

function resolveCode(
  code: string | null,
  options: string[],
): { code: string | null; rejected: boolean } {
  if (code == null) return { code: null, rejected: false }
  if (options.includes(code)) return { code, rejected: false }
  return { code: null, rejected: true }
}
